import { useMemo, useRef, useState } from "react";
import { Panel } from "@/components/ui/Panel";
import { LoadFailure } from "@/components/shared/LoadFailure";
import { useNavigate, useParams } from "react-router";
import type {
  FileResponse,
  LiteratureType,
  PositionsResponse,
} from "@patent-studio/api-types";
import { refusalDetail } from "@/services/api-client";
import { useTask, useTaskRefresh } from "@/hooks/use-tasks";
import {
  useNoveltyAnalysis,
  useRuleAdmission,
  useStartRun,
} from "@/hooks/use-novelty-analysis";
import { useNoveltyGate, useNoveltyGateFor } from "@/hooks/use-novelty-gate";
import { usePositions } from "@/hooks/use-positions";
import { useClassifyFile, useReplaceFile } from "@/hooks/use-prior-art";
import type { IntakeComponentSpec } from "@/lib/intake-components";
import {
  type IntakeSlotsRead,
  SLOTS_NOT_READ,
  useIntakeSlots,
} from "@/hooks/use-files";
import { useSlotUploads } from "@/hooks/use-slot-uploads";
import { stageHeadingLabel, tabPath } from "@/v2/stage-vocabulary";
import { DeleteConfirmation } from "./DeleteConfirmation";
import {
  DocumentDrawer,
  type DrawerDocument,
} from "../documents/DocumentDrawer";
import { OutOfDate } from "./analysis/OutOfDate";
import { PriorArtLibrary } from "./PriorArtLibrary";
import { PriorArtUpload } from "./PriorArtUpload";
import { RunFooter } from "./RunFooter";
import { StepCard } from "@/components/patterns/StepCard";
import { accountsOf, priorArtOf } from "./prior-art-list";
import { AdmittedArt } from "./AdmittedArt";
import { useDeleteFlow } from "./use-delete-flow";
import { StagePage } from "../layout/StagePage";
import { useQuestionGate } from "../questions/useQuestionGate";
import { StageSources } from "@/v2/layout/StageSources";

/**
 * The intake slot's own row for prior art, as the API serves it, so this
 * screen takes what intake takes and words a refusal the way intake words it.
 * Nothing while the table is on its way.
 */
function usePriorArtSpec(): {
  spec: IntakeComponentSpec | undefined;
  slots: IntakeSlotsRead;
} {
  const slots = useIntakeSlots();
  return {
    spec: slots.specs?.find((c) => c.fileType === "prior_art"),
    slots,
  };
}

/** What a row may do to its document, wired to the writes behind each. */
function useRowActions(
  taskId: string,
  accept: Record<string, string[]>,
  refresh: () => Promise<void>,
  onView: (file: FileResponse) => void,
) {
  const classify = useClassifyFile(taskId);
  const replace = useReplaceFile(taskId, refresh);
  const { data: analysis } = useNoveltyAnalysis(taskId);
  return {
    taskId,
    accept,
    accounts: accountsOf(analysis),
    onClassify: (fileId: string, literatureType: LiteratureType | null) =>
      classify.mutate({ fileId, literatureType }),
    onView,
    onReplace: (file: FileResponse, replacement: File) =>
      replace
        .mutateAsync({ fileId: file.file_id, replacement })
        .then(() => undefined),
  };
}

/**
 * The run starts here and is watched on the analysis tab. A refusal keeps the
 * attorney here, with the server's reason and, once the gate has been read
 * again, the conditions it names.
 */
function useRunStart(taskId: string) {
  const navigate = useNavigate();
  const run = useStartRun(taskId);
  const [refusal, setRefusal] = useState<string | null>(null);
  const start = () => {
    setRefusal(null);
    run.mutate(undefined, {
      onSuccess: () =>
        void navigate(tabPath(taskId, "thesis", "novelty", "analysis")),
      onError: (failure) =>
        setRefusal(
          refusalDetail(failure, "The analysis could not be started."),
        ),
    });
  };
  const { data: runGate } = useNoveltyGateFor(taskId);
  const { data: positions } = usePositions(taskId);
  // Praxis asks its questions before the run starts (lane L decision 30).
  const gate = useQuestionGate({
    taskId,
    trigger: "novelty_run",
    topic: "prior art",
    cta: "Run novelty analysis",
    commit: start,
    ready: runGate?.ready === true && runGate.analysis.status !== "running",
    reading: JSON.stringify(positions?.positions ?? []),
  });
  return {
    start: gate.press,
    starting: run.isPending,
    asking: gate.asking,
    refusal,
    card: gate.card,
  };
}

/**
/**
 * The drop area. What is uploaded here goes through the intake slot's own
 * hook, declared as a direct upload. A file is typed by the zone it was
 * dropped on once the server has taken it, through the same write the row's
 * pill makes; a file refused and sent again keeps the type it was dropped with.
 */
function PriorArtDrop({
  spec,
  taskId,
  refresh,
  error,
  onError,
}: {
  spec: IntakeComponentSpec;
  taskId: string;
  refresh: () => Promise<void>;
  error: string | null;
  onError: (message: string | null) => void;
}) {
  const classify = useClassifyFile(taskId);
  const typedRef = useRef(new Map<File, LiteratureType>());
  const uploads = useSlotUploads({
    spec,
    taskId,
    source: "direct_upload",
    keepRefused: true,
    onUploaded: (uploaded, file) => {
      const literatureType = typedRef.current.get(file);
      if (literatureType === undefined) return;
      typedRef.current.delete(file);
      classify.mutate({ fileId: uploaded.file_id, literatureType });
    },
    onError,
    onSettled: refresh,
    onInFlight: () => undefined,
    onStaged: () => undefined,
  });
  return (
    <PriorArtUpload
      spec={spec}
      busy={uploads.busy}
      refused={uploads.pending}
      error={error}
      onFilesSelected={(chosen, literatureType) => {
        onError(null);
        for (const file of chosen) typedRef.current.set(file, literatureType);
        uploads.select(chosen);
      }}
      onRejected={onError}
      onRetry={uploads.retry}
      onDiscard={uploads.discard}
    />
  );
}

/**
 * The out-of-date state on this tab, off the served gate. The same read the
 * footer makes; the query is shared, so this costs no request.
 */
function StaleAnalysis({
  taskId,
  files,
  positions,
  run,
}: {
  taskId: string;
  files: readonly FileResponse[];
  positions: PositionsResponse | undefined;
  run: ReturnType<typeof useRunStart>;
}) {
  const { data: gate } = useNoveltyGate(
    taskId,
    files,
    positions?.positions ?? [],
  );
  return (
    <OutOfDate
      stale={gate?.analysis.stale ?? false}
      confirmedCount={positions?.confirmed ?? 0}
      running={gate?.analysis.status === "running"}
      starting={run.starting}
      onRerun={run.start}
    />
  );
}

/**
 * The novelty page's second tab: the prior art library the positions are
 * assessed against, and the run.
 *
 * Files are server state, read off the task and driven by the worker over the
 * status stream, exactly as intake reads them. What is uploaded here goes
 * through the intake slot's own hook, declared as a direct upload so it is
 * told apart from what intake supplied.
 */
export function NoveltyPriorArtPane() {
  const { taskId } = useParams<{ taskId: string }>();
  // The route carries the id; without one there is no draft and nothing to draw.
  return taskId === undefined ? null : <PriorArtTab taskId={taskId} />;
}

/** The two things the tab opens over itself: a document, and the question before a delete. */
function DrawerAndConfirm({
  taskId,
  viewing,
  onCloseDrawer,
  deletion,
}: {
  taskId: string;
  viewing: DrawerDocument[] | null;
  onCloseDrawer: () => void;
  deletion: ReturnType<typeof useDeleteFlow>;
}) {
  return (
    <>
      <DocumentDrawer
        taskId={taskId}
        documents={viewing}
        onClose={onCloseDrawer}
      />
      <DeleteConfirmation
        request={deletion.request}
        onCancel={deletion.cancel}
        onConfirm={deletion.confirm}
      />
    </>
  );
}

/**
 * What the disclosure admits is known, read beside the library, with the
 * attorney's ruling on each entry. A run in flight holds the rulings, because
 * its landing writes the entries it read at its start.
 */
function AdmittedEntries({ taskId }: { taskId: string }) {
  const { data: analysis } = useNoveltyAnalysis(taskId);
  const rule = useRuleAdmission(taskId);
  return (
    <AdmittedArt
      entries={analysis?.admitted ?? []}
      busy={rule.isPending || analysis?.status === "running"}
      onRule={(entryId, ruling) => {
        rule.mutate({ entryId, ruling });
      }}
    />
  );
}

/**
 * The tab's one step: adding prior art, and the library it goes into. Its own
 * component so the pane reads as the step, the run footer and what opens over
 * them.
 */
function LibraryStep({
  failure,
  slots,
  spec,
  refresh,
  uploadError,
  onUploadError,
  files,
  loading,
  deletion,
  rowActions,
}: {
  failure: unknown;
  slots: IntakeSlotsRead;
  spec: IntakeComponentSpec | undefined;
  refresh: () => Promise<void>;
  uploadError: string | null;
  onUploadError: (message: string | null) => void;
  files: FileResponse[];
  loading: boolean;
  deletion: ReturnType<typeof useDeleteFlow>;
  rowActions: ReturnType<typeof useRowActions>;
}) {
  return (
    <StepCard
      number={2}
      title="Upload & Manage Prior Art"
      intro="Everything from intake is already loaded. Add, review or remove prior art before you run the analysis."
    >
      <Panel shape="row" pad="roomy">
        {failure !== null && (
          <LoadFailure
            error={failure}
            fallback="Your prior art could not be loaded."
            shape="row"
            className="mb-3"
          />
        )}
        {slots.failure !== null && (
          <LoadFailure
            error={slots.failure}
            fallback={SLOTS_NOT_READ}
            shape="row"
            className="mb-3"
            onRetry={slots.retry}
          />
        )}
        {spec !== undefined && (
          <PriorArtDrop
            spec={spec}
            taskId={rowActions.taskId}
            refresh={refresh}
            error={uploadError}
            onError={onUploadError}
          />
        )}
        <PriorArtLibrary
          files={files}
          // A row's replace takes what the slot takes, so the rows wait on the
          // table as the drop area does.
          loading={loading || (spec === undefined && slots.failure === null)}
          deleteError={deletion.error}
          onDeleteOne={deletion.askOne}
          onDeleteMany={deletion.askMany}
          {...rowActions}
        />
        <AdmittedEntries taskId={rowActions.taskId} />
      </Panel>
    </StepCard>
  );
}

function PriorArtTab({ taskId }: { taskId: string }) {
  const { data: task, isLoading, isError, error } = useTask(taskId);
  const { data: positions } = usePositions(taskId);

  const [uploadError, setUploadError] = useState<string | null>(null);
  // The file the drawer is open on, by id: the drawer reads the row as the
  // task now serves it, so a stamp that lands while it is open reaches the
  // header rather than a copy taken at the click.
  const [viewingId, setViewingId] = useState<string | null>(null);
  const refresh = useTaskRefresh(taskId);
  const { spec, slots } = usePriorArtSpec();

  const files = priorArtOf(task?.files);
  const deletion = useDeleteFlow(taskId, files);
  const rowActions = useRowActions(
    taskId,
    spec?.accept ?? {},
    refresh,
    (file) => {
      setViewingId(file.file_id);
    },
  );
  // Opened plain: a prior art row cites no passage, so nothing is marked.
  const viewing = useMemo<DrawerDocument[] | null>(() => {
    const file = files.find((f) => f.file_id === viewingId);
    return file === undefined ? null : [{ file, marked: null }];
  }, [files, viewingId]);
  const run = useRunStart(taskId);

  return (
    <StagePage
      measure="thesis"
      guide="prior-art-and-positions"
      testId="novelty-prior-art-pane"
      taskId={taskId}
      eyebrow={`${stageHeadingLabel("thesis")} · Add Prior Art`}
      title="Add and manage prior art"
      lede="Everything from intake is already loaded. Add, review or remove prior art before you run the novelty analysis."
    >
      <StageSources taskId={taskId} stage="novelty" />
      <StaleAnalysis
        taskId={taskId}
        files={files}
        positions={positions}
        run={run}
      />

      <LibraryStep
        failure={isError ? error : null}
        slots={slots}
        spec={spec}
        refresh={refresh}
        uploadError={uploadError}
        onUploadError={setUploadError}
        files={files}
        loading={isLoading}
        deletion={deletion}
        rowActions={rowActions}
      />

      {run.card ?? (
        <RunFooter
          taskId={taskId}
          files={files}
          positions={positions?.positions ?? []}
          onRun={run.start}
          starting={run.starting}
          asking={run.asking}
          refusal={run.refusal}
        />
      )}

      <DrawerAndConfirm
        taskId={taskId}
        viewing={viewing}
        onCloseDrawer={() => {
          setViewingId(null);
        }}
        deletion={deletion}
      />
    </StagePage>
  );
}
